import type { CommandItem } from "@replohq/lattice/utils/commands";

import React from "react";

import twMerge from "@v1/components/common/designSystem/utils/twMerge";
import { DEBUG_OPTIONS } from "@v2/features/debug/debugOptions";
import {
  applyDebugOptionStorageEffect,
  getDebugOptionStorageEffect,
} from "@v2/features/debug/debugOptionStorageEffects";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { useLocalStorage } from "@v2/hooks/useLocalStorage";

export function useDebugOptionsCommandItems(): Record<string, CommandItem> {
  const isDebugMode = useIsDebugMode();
  const localStorage = useLocalStorage();

  if (!isDebugMode) {
    return {};
  }

  const items: Record<string, CommandItem> = {};

  const toggleOptions = DEBUG_OPTIONS.filter(
    (option) => option.type !== "text" && !option.hiddenFromCommandMenu,
  );

  toggleOptions.forEach((option) => {
    const value = localStorage.getItem(option.key) === "true";

    items[option.key] = {
      label: `${option.label} (Currently ${value ? "ON" : "OFF"})`,
      icon: {
        type: "custom" as const,
        content: (
          <div className="flex h-[16px] w-[16px] items-center justify-center">
            <div
              className={twMerge(
                "h-[8px] w-[8px] rounded-full",
                value ? "bg-emerald-soft0" : "bg-surface",
              )}
            />
          </div>
        ),
      },
      onSelect: () => {
        const nextValue = !value;
        localStorage.setItem(option.key, JSON.stringify(nextValue));
        applyDebugOptionStorageEffect(
          getDebugOptionStorageEffect({ option, nextValue }),
        );
      },
    };
  });

  return items;
}
